import React, { useState, useMemo } from 'react';
import { Button } from 'modules/v2/common/AtomicDesign/atoms';

const SORT_ORDER = {
  MOST_RECENT: 'most_recent',
  ASCENDING: 'ascending',
  DESCENDING: 'descending',
};

const MAX_EXPORT_LIMIT = 15;

const TemplateSelectionModal = ({
  isOpen,
  onClose,
  templates = [],
  selectedTemplates = [],
  onToggleTemplate,
  onToggleAll,
  onDownload,
  isStartingExport = false,
}) => {
  const [sortOrder, setSortOrder] = useState(SORT_ORDER.MOST_RECENT);

  const sortedTemplates = useMemo(() => {
    if (sortOrder === SORT_ORDER.MOST_RECENT) {
      return [...templates];
    }

    const sorted = [...templates].sort((a, b) => {
      const titleA = a?.template_title || '';
      const titleB = b?.template_title || '';
      return titleA.localeCompare(titleB);
    });

    return sortOrder === SORT_ORDER.DESCENDING ? sorted.reverse() : sorted;
  }, [templates, sortOrder]);

  if (!isOpen) return null;

  const cycleSortOrder = () => {
    setSortOrder((current) => {
      if (current === SORT_ORDER.MOST_RECENT) return SORT_ORDER.ASCENDING;
      if (current === SORT_ORDER.ASCENDING) return SORT_ORDER.DESCENDING;
      return SORT_ORDER.MOST_RECENT;
    });
  };

  const getSortIcon = () => {
    if (sortOrder === SORT_ORDER.ASCENDING) {
      return (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" />
        </svg>
      );
    }
    if (sortOrder === SORT_ORDER.DESCENDING) {
      return (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h9m5-4v12m0 0l-4-4m4 4l4-4" />
        </svg>
      );
    }
    return (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
      </svg>
    );
  };

  const getSortLabel = () => {
    if (sortOrder === SORT_ORDER.ASCENDING) return 'A-Z';
    if (sortOrder === SORT_ORDER.DESCENDING) return 'Z-A';
    return 'Most Recent';
  };

  const allSelected = selectedTemplates.length === templates.length && templates.length > 0;
  const isOverLimit = selectedTemplates.length > MAX_EXPORT_LIMIT;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[80vh] overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-semibold text-gray-800">Download Templates</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(80vh-200px)]">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="select-all"
                checked={allSelected}
                onChange={onToggleAll}
                className="h-4 w-4 text-cyan-600 focus:ring-cyan-500 border-gray-300 rounded"
              />
              <label htmlFor="select-all" className="ml-2 text-sm font-medium text-gray-700">
                {allSelected ? 'Deselect All' : 'Select All'} ({selectedTemplates.length}/{templates.length})
              </label>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-600">Sort by:</span>
              <button
                onClick={cycleSortOrder}
                className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                title={`Sort order: ${getSortLabel()}`}
              >
                {getSortIcon()}
                <span>{getSortLabel()}</span>
              </button>
            </div>
          </div>

          {isOverLimit && (
            <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-md flex items-start gap-3">
              <svg className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <p className="text-sm text-yellow-800">
                More than {MAX_EXPORT_LIMIT} medias can not be exported at once
              </p>
            </div>
          )}

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {sortedTemplates.map((template) => (
              <div
                key={template.template_id}
                className={`border rounded-lg p-3 transition-all cursor-pointer ${
                  selectedTemplates.includes(template.template_id)
                    ? 'border-cyan-500 bg-cyan-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => onToggleTemplate(template.template_id)}
              >
                <div className="aspect-[3/4] bg-gray-100 rounded mb-2 overflow-hidden">
                  {template.thumbnail_url || template.smartThumbnailUrl ? (
                    <img
                      src={template.smartThumbnailUrl || template.thumbnail_url}
                      alt={template.template_title || template.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400">
                      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                  )}
                </div>
                <div className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    checked={selectedTemplates.includes(template.template_id)}
                    onChange={(e) => {
                      e.stopPropagation();
                      onToggleTemplate(template.template_id);
                    }}
                    className="h-4 w-4 text-cyan-600 focus:ring-cyan-500 border-gray-300 rounded mt-0.5"
                  />
                  <p className="text-sm text-gray-700 line-clamp-2 flex-1">
                    {template.template_title || template.name}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
          <Button type="outlined" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={onDownload}
            disabled={selectedTemplates.length === 0 || isStartingExport || isOverLimit}
            className={selectedTemplates.length === 0 || isStartingExport || isOverLimit ? 'opacity-50 cursor-not-allowed' : ''}
          >
            {isStartingExport ? (
              <>
                <svg className="w-5 h-5 mr-2 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Starting...
              </>
            ) : (
              <>
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Download {selectedTemplates.length > 0 ? `(${selectedTemplates.length})` : ''}
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TemplateSelectionModal;
